import { initialPreferencesAreMissing } from '../../controllers/cookie-controls/transformer';
import type {
	ConsentHubResponse,
	PrefetchedConsentCookieData,
	PrefetchedConsentPreferences,
} from '../../services/consent-hub-service/types';
import {
	checkIfTokenIsDefault,
	isLocaleCoveredByAnyPrivacyActs,
} from '../../services/consent-hub-service/utils';
import { PrefetchedPreferenceState } from '../../types';

export const shouldShowBannerInSSRContext = (initialPreferences?: PrefetchedConsentPreferences) => {
	if (initialPreferences === PrefetchedPreferenceState.INTERNAL) {
		// If we're in the "fetch internally" state, we have no prefetched data and we can't determine
		// if the user has consented or not yet since we need to fetch, so show the banner
		return true;
	}

	if (initialPreferencesAreMissing(initialPreferences)) {
		// No data at all, we need to fetch again, but hide on this pass
		return false;
	}
	// If prefetched data has a consentToken or the consents array is there and populated
	// we want to hide the banner, as the user has has made consents already.
	const prefetchedToken = (initialPreferences as PrefetchedConsentCookieData).consentToken;
	const prefetchedTokenIsDefault = checkIfTokenIsDefault(prefetchedToken || '');
	const prefetchedConsents = (initialPreferences as ConsentHubResponse).consents;

	// We should have initialPreferences here, and those should have a currentLocale regardless
	const prefetchedLocale = initialPreferences!.currentLocale;
	const localeRequiresBanner = isLocaleCoveredByAnyPrivacyActs(prefetchedLocale);

	const hasLegitConsentToken = prefetchedToken && !prefetchedTokenIsDefault;
	const hasLegitConsents = prefetchedConsents && prefetchedConsents.length > 0;
	const prefetchedDataHasConsents = Boolean(hasLegitConsentToken || hasLegitConsents);

	/*
    Checking against locale occurs in future lifecycle phases and is complicated,
    so we should defer to later lifecycle phases to make the official calculation.

    Given that, the states this function should handle are:
    1) User has consented, hide the banner
    2) User has not consented (has defaults) in Tier1/2, show the banner
    3) User has not consented (has defaults) in Tier 3, hide the banner

    Technically, there is a possibility for a more complicated scenario:
    - User consented in Tier 2 -> Tier 1, which would show the banner...
      But this complex calculation will be picked up later in the lifecycle if genuinely necessary
      so we'll optimistically give the same treatment as State 1 above, and hide the banner
  */

	const showBannerSinceUserHasNotConsented = !prefetchedDataHasConsents;
	return localeRequiresBanner ? showBannerSinceUserHasNotConsented : false;
};
